import { useState } from "react";
import { toast } from "sonner";
import { Search, Smartphone, Battery, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300";

export default function ProtectionRepairRequest() {
  const [lookup, setLookup] = useState({ contract_number: "", email: "" });
  const [contract, setContract] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [repairType, setRepairType] = useState("display");
  const [shipping, setShipping] = useState({ phone: "", street: "", zip: "", city: "" });
  const [submitting, setSubmitting] = useState(false);

  const doLookup = async (e) => {
    e.preventDefault();
    setChecking(true); setError(""); setContract(null);
    try {
      const { data } = await api.post("/protection/lookup", lookup);
      setContract(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Vertrag nicht gefunden.");
    } finally { setChecking(false); }
  };

  const canRepair = contract && contract.status === "aktiv" && contract.repairs_remaining_this_year > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!shipping.phone.trim() || !shipping.street.trim() || !shipping.zip.trim() || !shipping.city.trim()) {
      toast.error("Bitte fülle alle Versanddaten aus.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: order } = await api.post("/protection/repair-order", {
        contract_number: lookup.contract_number, email: lookup.email, repair_type: repairType, ...shipping,
      });
      const { data: checkout } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
      window.location.href = checkout.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Beauftragung fehlgeschlagen.");
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="protection-repair" className="relative z-10 bg-[#0a0a0a] py-24 lg:py-32 border-t border-white/10 min-h-[70vh]">
      <div className="max-w-2xl mx-auto px-6 lg:px-10">
        <ShieldCheck size={28} className="text-white mb-5" />
        <h1 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl leading-[0.95] mb-10">
          Reparatur über dein Schutzpaket
        </h1>

        {!contract && (
          <form onSubmit={doLookup} data-testid="protection-repair-lookup-form" className="space-y-4">
            <input data-testid="protection-repair-contract-number" value={lookup.contract_number}
              onChange={(e) => setLookup((p) => ({ ...p, contract_number: e.target.value }))}
              placeholder="Vertragsnummer, z. B. SP-000001" className={inputCls} />
            <input data-testid="protection-repair-email" type="email" value={lookup.email}
              onChange={(e) => setLookup((p) => ({ ...p, email: e.target.value }))}
              placeholder="E-Mail" className={inputCls} />
            {error && <p data-testid="protection-repair-lookup-error" className="text-red-400 text-sm">{error}</p>}
            <button type="submit" disabled={checking} data-testid="protection-repair-lookup-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#050505] py-4 font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
              <Search size={16} /> {checking ? "Suche…" : "Vertrag finden"}
            </button>
          </form>
        )}

        {contract && !canRepair && (
          <div data-testid="protection-repair-blocked" className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-6 text-sm text-amber-200">
            {contract.status !== "aktiv"
              ? "Dieser Vertrag ist nicht (mehr) aktiv."
              : `Dein Jahreskontingent (${contract.repairs_per_year} Reparatur(en)) für dieses Vertragsjahr ist bereits aufgebraucht.`}
          </div>
        )}

        {canRepair && (
          <form onSubmit={submit} data-testid="protection-repair-order-form" className="space-y-5">
            <div className="rounded-2xl border border-white/10 bg-[#050505] p-5 text-sm">
              <p className="text-[#888]">Gerät</p>
              <p className="text-white mt-1">{contract.device}</p>
              <p className="text-emerald-400 text-xs mt-2">{contract.repairs_remaining_this_year} von {contract.repairs_per_year} Reparatur(en) in diesem Vertragsjahr verfügbar</p>
            </div>

            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Was ist defekt?</label>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setRepairType("display")} data-testid="protection-repair-type-display"
                  className={`rounded-xl border p-4 flex flex-col items-center gap-2 transition-colors ${repairType === "display" ? "border-white bg-white/10" : "border-white/10 text-[#888] hover:border-white/30"}`}>
                  <Smartphone size={20} /> <span className="text-sm">Display</span>
                </button>
                <button type="button" onClick={() => setRepairType("akku")} data-testid="protection-repair-type-akku"
                  className={`rounded-xl border p-4 flex flex-col items-center gap-2 transition-colors ${repairType === "akku" ? "border-white bg-white/10" : "border-white/10 text-[#888] hover:border-white/30"}`}>
                  <Battery size={20} /> <span className="text-sm">Akku</span>
                </button>
              </div>
            </div>

            <input data-testid="protection-repair-phone" value={shipping.phone} onChange={(e) => setShipping((p) => ({ ...p, phone: e.target.value }))} placeholder="Telefon" className={inputCls} />
            <input data-testid="protection-repair-street" value={shipping.street} onChange={(e) => setShipping((p) => ({ ...p, street: e.target.value }))} placeholder="Straße & Hausnummer (Versandadresse)" className={inputCls} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input data-testid="protection-repair-zip" value={shipping.zip} onChange={(e) => setShipping((p) => ({ ...p, zip: e.target.value }))} placeholder="PLZ" className={inputCls} />
              <input data-testid="protection-repair-city" value={shipping.city} onChange={(e) => setShipping((p) => ({ ...p, city: e.target.value }))} placeholder="Ort" className={inputCls} />
            </div>

            <button type="submit" disabled={submitting} data-testid="protection-repair-submit"
              className="w-full inline-flex items-center justify-center rounded-full bg-white text-[#050505] py-4 font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
              {submitting ? "Wird weitergeleitet…" : "Reparatur beauftragen (nur Versand kostenpflichtig)"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
